import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { TableLoaderRow } from "@/components/ui/loader";
import { useDepreciationSchedule } from "@/features/finance/api";
import { currentFy, fyOptions } from "@/features/finance/fy";
import { inr } from "@/features/finance/format";
import { ReportExportButton } from "@/features/reports/export/ReportExportButton";
import { useBranch } from "@/context/BranchContext";

export const Route = createFileRoute("/_authenticated/finance/depreciation-schedule")({
  component: DepreciationSchedulePage,
});

const BOOK_OPTIONS = [
  { value: "FINANCIAL",  label: "Financial" },
  { value: "INCOME_TAX", label: "Income Tax (IT Act)" },
  { value: "MANAGEMENT", label: "Management" },
];

function DepreciationSchedulePage() {
  const [fy, setFy]     = useState(currentFy());
  const [book, setBook] = useState("FINANCIAL");
  const { currentBranchId } = useBranch();
  const { data, isLoading } = useDepreciationSchedule(fy, book, currentBranchId);
  const rows = data ?? [];

  const totals = rows.reduce(
    (a, r) => ({
      gross:    a.gross    + r.gross_block,
      depOpen:  a.depOpen  + r.accum_dep_opening,
      depFy:    a.depFy    + r.dep_this_fy,
      depClose: a.depClose + r.accum_dep_closing,
      nbv:      a.nbv      + r.nbv_closing,
    }),
    { gross: 0, depOpen: 0, depFy: 0, depClose: 0, nbv: 0 }
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Depreciation Schedule</h1>
          <p className="text-sm text-muted-foreground">
            Asset-wise depreciation summary &middot; FY {fy} &middot; {rows.length} assets
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={fy} onValueChange={setFy}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>{fyOptions().map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={book} onValueChange={setBook}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BOOK_OPTIONS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <ReportExportButton
            reportKey="R-DEP-SCH"
            title={`Depreciation Schedule FY ${fy}`}
            filename={`depreciation-schedule-${fy}-${book}`}
            rows={() => rows.map((r) => ({
              "Asset Code":          r.asset_code,
              "Asset Name":          r.asset_name,
              "Category":            r.category_name ?? "",
              "Cap Date":            r.capitalisation_date,
              "Method":              r.method,
              "Rate (%)":            r.dep_rate,
              "Gross Block":         r.gross_block,
              "Acc Dep Opening":     r.accum_dep_opening,
              "Dep This FY":         r.dep_this_fy,
              "Acc Dep Closing":     r.accum_dep_closing,
              "NBV Closing":         r.nbv_closing,
              "Remaining Life (yrs)": r.remaining_life_years ?? "",
            }))}
            requiredColumns={["Asset Code"]}
            variant="outline"
            disabled={!rows.length}
          />
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Schedule</CardTitle></CardHeader>
        <CardContent>
          <div className="max-h-[72vh] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Asset</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Rate %</TableHead>
                  <TableHead className="text-right">Gross Block</TableHead>
                  <TableHead className="text-right">Acc Dep Open</TableHead>
                  <TableHead className="text-right">Dep This FY</TableHead>
                  <TableHead className="text-right">Acc Dep Close</TableHead>
                  <TableHead className="text-right">NBV Closing</TableHead>
                  <TableHead className="text-right">Rem. Life</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableLoaderRow colSpan={11} />}
                {rows.map((r) => (
                  <TableRow key={r.asset_id}>
                    <TableCell className="font-mono text-xs">{r.asset_code}</TableCell>
                    <TableCell>{r.asset_name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{r.category_name ?? "�"}</TableCell>
                    <TableCell>
                      <Badge variant={r.method === "SLM" ? "secondary" : "outline"}>{r.method}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.dep_rate.toFixed(2)}%</TableCell>
                    <TableCell className="text-right">{inr(r.gross_block)}</TableCell>
                    <TableCell className="text-right">{inr(r.accum_dep_opening)}</TableCell>
                    <TableCell className="text-right font-medium text-orange-600 dark:text-orange-400">
                      {inr(r.dep_this_fy)}
                    </TableCell>
                    <TableCell className="text-right">{inr(r.accum_dep_closing)}</TableCell>
                    <TableCell className="text-right font-semibold">{inr(r.nbv_closing)}</TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {r.remaining_life_years != null ? `${r.remaining_life_years} yrs` : "�"}
                    </TableCell>
                  </TableRow>
                ))}
                {!isLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-muted-foreground py-10">
                      No capitalised assets found for FY {fy}.
                    </TableCell>
                  </TableRow>
                )}
                {rows.length > 0 && (
                  <TableRow className="bg-muted/40 font-semibold border-t-2">
                    <TableCell colSpan={5}>Totals</TableCell>
                    <TableCell className="text-right">{inr(totals.gross)}</TableCell>
                    <TableCell className="text-right">{inr(totals.depOpen)}</TableCell>
                    <TableCell className="text-right text-orange-600 dark:text-orange-400">{inr(totals.depFy)}</TableCell>
                    <TableCell className="text-right">{inr(totals.depClose)}</TableCell>
                    <TableCell className="text-right">{inr(totals.nbv)}</TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
